/**
 * Capture an existing group into group-template content (requirements F2.2,
 * F2.3): the group's items page by page, each item's subitems, and optionally
 * their updates: text with line breaks, and their user and team mentions (read
 * from monday's mention links in the update's HTML). Stops at the template limits and reports what
 * it had to leave out, so the editor can warn instead of truncating silently.
 *
 * Reads go through lib/mondayApi; a refused query (missing scope) is thrown
 * with monday's wording so the picker can show it. One refusal is absorbed:
 * when only the `updates` fields are refused (the viewing user's authorization
 * of the app predates its updates:read scope, and only an admin can approve it), the group is read again without updates and the
 * result says so (`updatesRefused`), so the import still lands.
 */
import { isSupportedColumn } from '../Config';
import { newKey } from './groupContent';
import { GROUP_LIMITS } from './templateLimits';
import { describeApiErrors } from './useBoardColumns';

export const PAGE_SIZE = 50;

const BLOCKS = new Set(['P', 'DIV', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'TR']);
const MENTION_TYPES = new Set(['User', 'Team']);

/**
 * An update's HTML (monday's `body`) → the template's update shape:
 * { body: text with line breaks, mentions: [{ id, type, name }] }.
 *
 * A user or team mention is monday's anchor
 *   <a class="user_mention_editor" data-mention-type="User" data-mention-id="123">@Name</a>
 * and becomes "@Name" in the text plus a mention, which is how the editor and
 * lib/updates keep them. Other mentions (boards, projects) stay as text.
 * Parsed with DOMParser, an inert document: nothing in the HTML loads or runs.
 */
export function updateFromHtml(html) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const mentions = [];
  const seen = new Set();
  let text = '';
  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        text += child.nodeValue;
        return;
      }
      if (child.nodeType !== 1) return;
      const type = child.getAttribute('data-mention-type');
      const id = child.getAttribute('data-mention-id');
      if (child.tagName === 'A' && MENTION_TYPES.has(type) && id) {
        const name = child.textContent.replace(/^@/, '').trim();
        text += `@${name}`;
        const key = `${type}:${id}`;
        if (name && !seen.has(key)) {
          seen.add(key);
          mentions.push({ id, type, name });
        }
        return;
      }
      if (child.tagName === 'BR') {
        text += '\n';
        return;
      }
      const block = BLOCKS.has(child.tagName);
      if (block && text && !text.endsWith('\n')) text += '\n';
      walk(child);
      if (block && !text.endsWith('\n')) text += '\n';
    });
  };
  walk(doc.body);
  const body = text
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { body, mentions };
}

export const GROUPS_QUERY = `query ($boardId: [ID!]) {
  boards (ids: $boardId) { groups { id title color } }
}`;

const CELLS = 'column_values { id type value text }';
const itemFields = (withUpdates, limits) => {
  const updates = withUpdates ? `updates (limit: ${limits.updatesPerRow}) { body text_body }` : '';
  return `id name ${CELLS} ${updates} subitems { id name ${CELLS} ${updates} }`;
};

export const firstPageQuery = (withUpdates, limits = GROUP_LIMITS) => `query ($boardId: [ID!], $groupId: [String], $limit: Int!) {
  boards (ids: $boardId) { groups (ids: $groupId) { id title color
    items_page (limit: $limit) { cursor items { ${itemFields(withUpdates, limits)} } }
  } }
}`;

export const nextPageQuery = (withUpdates, limits = GROUP_LIMITS) => `query ($cursor: String!, $limit: Int!) {
  next_items_page (cursor: $cursor, limit: $limit) { cursor items { ${itemFields(withUpdates, limits)} } }
}`;

const UNAUTHORIZED_FIELD = /Unauthorized to load field '([^']+)'/;

/**
 * The fields monday refused for a missing scope, e.g.
 *   Unauthorized to load field 'Query.boards.groups.items_page.items.updates',
 *   Reason: missing required scopes.  (code UNAUTHORIZED_FIELD_OR_TYPE)
 * → ['Query.boards.groups.items_page.items.updates']. monday sends one error per
 * row it resolved, so a group of 50 items repeats the same few fields; they are
 * deduplicated here. [] when nothing was refused for a scope.
 */
export function refusedFields(res) {
  const errors = Array.isArray(res && res.errors) ? res.errors : [];
  const fields = new Set();
  for (const e of errors) {
    const message = (e && e.message) || '';
    const code = e && e.extensions && e.extensions.code;
    if (code !== 'UNAUTHORIZED_FIELD_OR_TYPE' && !/missing required scopes/i.test(message)) continue;
    const m = message.match(UNAUTHORIZED_FIELD);
    fields.add(m ? m[1] : '');
  }
  return [...fields];
}

async function ask(api, query, variables) {
  const res = await api.api(query, { variables });
  const error = describeApiErrors(res);
  if (error) {
    const err = new Error(error);
    err.refusedFields = refusedFields(res);
    err.missingScope = err.refusedFields.length > 0;
    throw err;
  }
  return res && res.data;
}

/** Every refused field is an `updates` field: the read works without them. */
const onlyUpdatesRefused = (e) =>
  Boolean(e && e.missingScope) && e.refusedFields.every((f) => /\.updates$/.test(f));

/** The board's groups, in board order: [{ id, title, color }]. */
export async function fetchGroups(api, boardId) {
  const data = await ask(api, GROUPS_QUERY, { boardId: [String(boardId)] });
  const board = data && data.boards && data.boards[0];
  if (!board) throw new Error('The board could not be read');
  return board.groups || [];
}

const hasValue = (cv) => cv && cv.value !== null && cv.value !== undefined && cv.value !== '';

/** Cells worth keeping: a supported column, a value, and a column the template knows. */
function usableCells(cells, knownIds) {
  return (cells || [])
    .filter((cv) => hasValue(cv) && isSupportedColumn(cv.type) && knownIds.has(String(cv.id)))
    .map((cv) => ({ id: cv.id, type: cv.type, value: cv.value, text: cv.text == null ? '' : cv.text }));
}

/**
 * captureGroup(api, { boardId, groupId, includeUpdates, itemColumns, subitemColumns })
 *   → { content, truncated: { items, subitems, updates, morePages }, updatesRefused }
 *
 * updatesRefused: updates were asked for, monday refused them for a missing
 * scope, and the group was captured without them.
 *
 * itemColumns / subitemColumns are the two boards' column lists; the content's
 * column lists are those columns that actually carry a value in what was read.
 */
export async function captureGroup(
  api,
  { boardId, groupId, includeUpdates = true, itemColumns = [], subitemColumns = [], limits = GROUP_LIMITS, pageSize = PAGE_SIZE },
) {
  const itemIds = new Set(itemColumns.map((c) => String(c.id)));
  const subIds = new Set(subitemColumns.map((c) => String(c.id)));
  const truncated = { items: 0, subitems: 0, updates: 0, morePages: false };
  const items = [];
  let group = null;
  let subitemsTotal = 0;
  let updatesTotal = 0;
  let readUpdates = includeUpdates;
  let updatesRefused = false;

  const takeUpdates = (raw, what) => {
    const out = [];
    for (const u of raw || []) {
      const update = u && u.body ? updateFromHtml(u.body) : { body: String((u && u.text_body) || '').trim(), mentions: [] };
      if (!update.body) continue;
      if (out.length >= limits.updatesPerRow || updatesTotal >= limits.updatesTotal) {
        truncated.updates += 1;
        continue;
      }
      out.push(update);
      updatesTotal += 1;
    }
    return out;
  };

  const convert = (it) => {
    // the item's own updates come before its subitems' in the total, as on the board
    const updates = readUpdates ? takeUpdates(it.updates) : [];
    const subitems = [];
    for (const s of it.subitems || []) {
      if (subitems.length >= limits.subitemsPerItem || subitemsTotal >= limits.subitemsTotal) {
        truncated.subitems += 1;
        continue;
      }
      subitemsTotal += 1;
      subitems.push({
        key: newKey('s'),
        name: s.name || '',
        column_values: usableCells(s.column_values, subIds),
        updates: readUpdates ? takeUpdates(s.updates) : [],
      });
    }
    return {
      key: newKey('i'),
      name: it.name || '',
      column_values: usableCells(it.column_values, itemIds),
      updates,
      subitems,
    };
  };

  let cursor = null;
  let first = true;
  for (;;) {
    let page;
    if (first) {
      const variables = { boardId: [String(boardId)], groupId: [String(groupId)], limit: pageSize };
      let data;
      try {
        data = await ask(api, firstPageQuery(readUpdates, limits), variables);
      } catch (e) {
        if (!readUpdates || !onlyUpdatesRefused(e)) throw e;
        readUpdates = false;
        updatesRefused = true;
        data = await ask(api, firstPageQuery(false, limits), variables);
      }
      const g = data && data.boards && data.boards[0] && data.boards[0].groups && data.boards[0].groups[0];
      if (!g) throw new Error('The group could not be read');
      group = { title: g.title || '', color: g.color || '' };
      page = g.items_page;
      first = false;
    } else {
      const data = await ask(api, nextPageQuery(readUpdates, limits), { cursor, limit: pageSize });
      page = data && data.next_items_page;
    }
    for (const it of (page && page.items) || []) {
      if (items.length >= limits.items) {
        truncated.items += 1;
        continue;
      }
      items.push(convert(it));
    }
    cursor = page && page.cursor;
    if (!cursor) break;
    if (items.length >= limits.items) {
      truncated.morePages = true; // more pages exist; they are not read just to be counted
      break;
    }
  }

  const usedIn = (rows, pick) => {
    const used = new Set();
    for (const r of rows) for (const cv of pick(r)) used.add(String(cv.id));
    return used;
  };
  const usedItemIds = usedIn(items, (it) => it.column_values);
  const usedSubIds = usedIn(
    items.flatMap((it) => it.subitems),
    (s) => s.column_values,
  );
  const bare = ({ id, title, type }) => ({ id, title, type });

  return {
    content: {
      group,
      item_columns: itemColumns.filter((c) => usedItemIds.has(String(c.id))).map(bare),
      subitem_columns: subitemColumns.filter((c) => usedSubIds.has(String(c.id))).map(bare),
      items,
    },
    truncated,
    updatesRefused,
  };
}

/** One sentence for the editor when anything was left out, or null. */
export function truncationNote(truncated, limits = GROUP_LIMITS) {
  if (!truncated) return null;
  const parts = [];
  if (truncated.items || truncated.morePages) parts.push(`the first ${limits.items} items`);
  if (truncated.subitems) parts.push(`${truncated.subitems} subitem${truncated.subitems === 1 ? '' : 's'} beyond the limits were left out`);
  if (truncated.updates) parts.push(`${truncated.updates} update${truncated.updates === 1 ? '' : 's'} beyond the limits were left out`);
  if (!parts.length) return null;
  return `This group is larger than a template can hold: ${parts.join('; ')}. Everything imported is in board order.`;
}

const PERMISSION_HINT =
  'Ask an admin of your monday account to approve the app’s new permissions from the banner at the top of this view, then try again.';

/** The note for an import that landed without its updates because monday refused them. */
export const UPDATES_REFUSED_NOTE =
  'The group was imported without its updates. Importing updates is new in this version of the app and needs a permission that hasn’t been approved yet. ' +
  'Ask an admin of your monday account to approve the app’s new permissions from the banner at the top of this view, then import again.';

/**
 * The note for an import that failed. A missing scope gets a sentence that says
 * what to do rather than monday's per-row wall of "Unauthorized to load field";
 * anything else keeps monday's wording.
 */
export function importErrorNote(e) {
  if (e && e.missingScope) return `Could not import the group: this version of the app needs a permission that hasn’t been approved yet. ${PERMISSION_HINT}`;
  return `Could not import the group. monday said: “${(e && e.message) || 'request failed'}”.`;
}
